<template>
  <svg
    :viewBox="`0 0 ${VW} ${VH}`"
    width="100%"
    style="display: block; overflow: visible"
  >
    <defs>
      <linearGradient id="acetSide" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stop-color="rgba(245,235,210,0.95)" />
        <stop offset="40%" stop-color="rgba(218,200,165,0.88)" />
        <stop offset="100%" stop-color="rgba(185,162,125,0.80)" />
      </linearGradient>
      <linearGradient id="acetSideEdge" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="rgba(255,248,228,1)" />
        <stop offset="100%" stop-color="rgba(160,135,95,1)" />
      </linearGradient>
      <linearGradient id="templeSide" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stop-color="rgba(235,218,180,0.95)" />
        <stop offset="55%" stop-color="rgba(205,182,138,0.90)" />
        <stop offset="100%" stop-color="rgba(168,145,105,0.80)" />
      </linearGradient>
      <linearGradient id="templeTop" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="rgba(255,250,235,0.55)" />
        <stop offset="100%" stop-color="rgba(0,0,0,0)" />
      </linearGradient>
      <linearGradient id="lnsSide2" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#1c1c1c" />
        <stop offset="100%" stop-color="#050505" />
      </linearGradient>
      <linearGradient id="hSide" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="#e8d490" />
        <stop offset="50%" stop-color="#b8922a" />
        <stop offset="100%" stop-color="#8a6a18" />
      </linearGradient>
      <filter id="sideShad" x="-5%" y="-10%" width="110%" height="130%">
        <feDropShadow
          dx="0"
          dy="8"
          stdDeviation="12"
          flood-color="rgba(0,0,0,0.60)"
        />
      </filter>
      <filter id="templeShad" x="-2%" y="-10%" width="104%" height="130%">
        <feDropShadow
          dx="0"
          dy="6"
          stdDeviation="9"
          flood-color="rgba(0,0,0,0.50)"
        />
      </filter>
      <marker
        id="sah2"
        markerWidth="7"
        markerHeight="7"
        refX="6"
        refY="3.5"
        orient="auto"
      >
        <path d="M0,1 L6,3.5 L0,6Z" :fill="ac" />
      </marker>
      <marker
        id="sat2"
        markerWidth="7"
        markerHeight="7"
        refX="1"
        refY="3.5"
        orient="auto"
      >
        <path d="M6,1 L0,3.5 L6,6Z" :fill="ac" />
      </marker>
    </defs>

    <!-- TEMPLE ARM -->
    <path
      :d="`M ${hingeX + 2} ${hingeY - templeH / 2} L ${tEndX} ${
        tEndY - templeH * 0.38
      } L ${tEndX} ${tEndY + templeH * 0.62} L ${hingeX + 2} ${
        hingeY + templeH / 2
      } Z`"
      fill="url(#templeSide)"
      stroke="url(#acetSideEdge)"
      stroke-width="1.2"
      filter="url(#templeShad)"
    />
    <!-- Temple top sheen -->
    <path
      :d="`M ${hingeX + 4} ${hingeY - templeH / 2 + 1} L ${tEndX - 12} ${
        tEndY - templeH * 0.38 + 1
      } L ${tEndX - 12} ${tEndY - templeH * 0.38 + 4} L ${hingeX + 4} ${
        hingeY - templeH / 2 + 4
      } Z`"
      fill="url(#templeTop)"
      stroke="none"
    />
    <!-- Temple tip -->
    <ellipse
      :cx="tEndX"
      :cy="tEndY + (templeH * 0.62 - templeH * 0.38) / 2"
      rx="4"
      :ry="templeH / 2"
      fill="url(#templeSide)"
      stroke="url(#acetSideEdge)"
      stroke-width="1"
    />

    <!-- Size numbers stamped on temple -->
    <g :transform="`rotate(${angleDeg}, ${stampX}, ${stampY})`">
      <rect
        :x="stampX - stampW / 2"
        :y="stampY - 8"
        :width="stampW"
        height="14"
        rx="3"
        fill="rgba(0,0,0,0.18)"
      />
      <text
        :x="stampX"
        :y="stampY + 3.5"
        text-anchor="middle"
        font-size="9.5"
        font-weight="600"
        fill="rgba(240,218,160,0.95)"
        font-family="HE Manrope, Arial, sans-serif"
        letter-spacing="2"
      >
        {{ r(vals.lensW) }} □ {{ r(vals.bridge) }} — {{ r(vals.temple) }}
      </text>
    </g>

    <!-- LENS PROFILE BLOCK -->
    <rect
      :x="ox + 4"
      :y="oy + 4"
      :width="lpw - 8"
      :height="lhp - 8"
      rx="4"
      fill="url(#lnsSide2)"
    />
    <rect
      :x="ox"
      :y="oy"
      :width="lpw"
      :height="lhp"
      rx="6"
      fill="url(#acetSide)"
      stroke="url(#acetSideEdge)"
      stroke-width="5"
      fill-opacity="0"
      filter="url(#sideShad)"
    />
    <rect
      :x="ox + 2"
      :y="oy + 2"
      :width="lpw - 4"
      :height="lhp * 0.35"
      rx="5"
      fill="rgba(255,252,238,0.22)"
      stroke="none"
    />

    <!-- HINGE -->
    <rect
      :x="hingeX - 2"
      :y="hingeY - 10"
      width="11"
      height="20"
      rx="2.5"
      fill="url(#hSide)"
      stroke="rgba(255,235,150,0.35)"
      stroke-width="0.8"
    />
    <circle :cx="hingeX + 3" :cy="hingeY - 3.5" r="2" fill="rgba(0,0,0,0.3)" />
    <circle :cx="hingeX + 3" :cy="hingeY + 4.5" r="2" fill="rgba(0,0,0,0.3)" />

    <!-- TEMPLE LENGTH annotation -->
    <g
      @mouseenter="$emit('label-hover', 'temple')"
      @mouseleave="$emit('label-hover', null)"
      style="cursor: default"
    >
      <line
        :x1="hingeX"
        :y1="annY + 10"
        :x2="hingeX"
        :y2="oy"
        :stroke="dl"
        stroke-width="1"
        stroke-dasharray="3 3"
      />
      <line
        :x1="tEndX"
        :y1="annY + 10"
        :x2="tEndX"
        :y2="tEndY - templeH / 2 - 3"
        :stroke="dl"
        stroke-width="1"
        stroke-dasharray="3 3"
      />
      <line
        :x1="hingeX"
        :y1="annY"
        :x2="tEndX"
        :y2="annY"
        :stroke="bc"
        stroke-width="1.6"
        marker-start="url(#sat2)"
        marker-end="url(#sah2)"
      />
      <text
        :x="hingeX + tPx * 0.5"
        :y="annY - 11"
        text-anchor="middle"
        font-size="9.5"
        font-weight="600"
        :fill="bc"
        letter-spacing="1.3"
        font-family="HE Manrope, Arial, sans-serif"
      >
        TEMPLE LENGTH
      </text>
      <text
        :x="hingeX + tPx * 0.5"
        :y="annY - 24"
        text-anchor="middle"
        font-size="13"
        font-weight="700"
        fill="rgba(220,185,90,1)"
        letter-spacing="0.5"
        font-family="HE Manrope, Arial, sans-serif"
      >
        {{ r(vals.temple) }} mm
      </text>
    </g>
  </svg>
</template>

<script>
export default {
  name: "GlassesSide",
  props: {
    vals: { type: Object, required: true },
    activeLabel: { type: String, default: null },
  },
  emits: ["label-hover"],
  data() {
    return {
      VW: 680,
      VH: 200,
      templeH: 11,
      ac: "rgba(200,162,74,0.9)",
      dim: "rgba(190,155,74,0.38)",
    };
  },
  computed: {
    S() {
      return Math.min(2.6, (this.VW - 120) / this.vals.temple);
    },
    lhp() {
      return this.vals.lensH * this.S;
    },
    lpw() {
      return this.lhp * 0.28;
    },
    tPx() {
      return this.vals.temple * this.S;
    },
    totalW() {
      return this.lpw + this.tPx;
    },
    ox() {
      return Math.max(28, (this.VW - this.totalW) / 2);
    },
    oy() {
      return (this.VH - this.lhp) / 2 + 10;
    },
    hingeX() {
      return this.ox + this.lpw;
    },
    hingeY() {
      return this.oy + this.lhp * 0.2;
    },
    tEndX() {
      return this.hingeX + this.tPx;
    },
    tEndY() {
      return this.hingeY + this.tPx * 0.055;
    },
    annY() {
      return this.oy - 42;
    },
    tmy() {
      return (this.hingeY + this.tEndY) / 2;
    },
    angleDeg() {
      const rad = Math.atan2(
        this.tEndY - this.hingeY,
        this.tEndX - this.hingeX
      );
      return (rad * 180) / Math.PI;
    },
    stampX() {
      return this.hingeX + this.tPx * 0.455;
    },
    stampY() {
      return this.tmy;
    },
    stampW() {
      return this.tPx * 0.55;
    },
    isActive() {
      return this.activeLabel === "temple";
    },
    bc() {
      return this.isActive ? "rgba(220,185,90,1)" : this.ac;
    },
    dl() {
      return this.isActive ? "rgba(210,175,80,0.7)" : this.dim;
    },
  },
  methods: {
    r(v) {
      return Math.round(v);
    },
  },
};
</script>
